import { useEffect, useState } from "react";
import { TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import DateRangePicker from "components/DateRangePicker";
import RegionAutoComplete from "components/SpecificAutoComplete/RegionAutoComplete";
import { Button } from "components/Button";
import { TReportFilters } from "../types";

type TProps = {
  filters: TReportFilters;
  onApply: (values: TReportFilters) => void;
  isLoading?: boolean;
  showTopN?: boolean;
};

/**
 * Shared filter bar for report pages: date range, region, optional topN, search.
 *
 * Laid out as a single row that shares the available width, wrapping only below
 * `xl`. Two of the children carry widths that fight that, so both are overridden
 * here rather than in the shared components:
 *  - DateRangePicker declares `min-w-[412px]`
 *  - RegionAutoComplete declares `w-full` over a 3-column grid
 * Giving them fixed pixel widths (the previous `w-[300px]` on each) either lost
 * to those declarations or squeezed the region grid to ~95px per select.
 */
export function ReportFiltersBar({
  filters,
  onApply,
  isLoading,
  showTopN,
}: TProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<TReportFilters>(filters);

  // re-sync when the URL-driven filters change (e.g. first resolve, back/forward)
  useEffect(() => {
    setPending(filters);
  }, [filters]);

  return (
    <div className="w-full px-4 py-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2 xl:flex-nowrap">
        <DateRangePicker
          key={`${filters.startD}_${filters.endD}`}
          className="!min-w-[264px] flex-1 xl:max-w-[330px]"
          initFromDate={pending.startD}
          initToDate={pending.endD}
          onChange={(value) => {
            setPending((prev) => ({
              ...prev,
              startD: value[0]?.split("T")[0] || prev.startD,
              endD: value[1]?.split("T")[0] || prev.endD,
            }));
          }}
          disabled={isLoading}
        />

        <RegionAutoComplete
          key={`${filters.provinceId}_${filters.cityId}_${filters.jurisdictionId}`}
          parentBackgroundColor={"bg-neutral-15"}
          defaultValue={{
            provinceId: filters.provinceId,
            cityId: filters.cityId,
            jurisdictionId: filters.jurisdictionId,
          }}
          onChange={(value) => {
            setPending((prev) => ({
              ...prev,
              provinceId: value?.province?.id || "",
              cityId: value?.city?.id || "",
              jurisdictionId: value?.jurisdiction?.id || "",
            }));
          }}
          className="!w-auto min-w-[300px] max-w-[400px] flex-[1.6]"
          disabled={isLoading}
        />

        {showTopN && (
          <TextField
            label={t("topN")}
            placeholder={t("topN")}
            size="small"
            type="number"
            className="w-[88px] shrink-0"
            value={pending.topN ?? 10}
            onChange={(e) =>
              setPending((prev) => ({
                ...prev,
                topN: Number(e.target.value) || 10,
              }))
            }
            disabled={isLoading}
          />
        )}

        <Button
          onClick={() => onApply(pending)}
          variant={"outlined"}
          icon={"MdSearch"}
          className="shrink-0 mr-4"
        >
          {t("search")}
        </Button>
      </div>
    </div>
  );
}

export default ReportFiltersBar;
